import { defineCrestModule } from '@crest/platform-contracts/modules';
import { platformMyDayHostCapability, platformOptionalFactProviderHostCapability, } from '@crest/platform-contracts/hosts';
import { projectsProjectDetailHostCapability } from '@crest/module-projects/contracts/detail-host';
import { projectsProjectDetailLauncherCapability } from '@crest/module-projects/contracts/detail-launcher';
import { projectsProjectRecordPolicyCapability } from '@crest/module-projects/contracts/record-policy';
import { mitigationDryingReadingsCapability, mitigationDryingSizingCapability, mitigationDryingWorkspaceCapability, } from './contracts/mitigation-drying-workspace.contract.js';
import { mitigationDryingReportDataCapability } from './contracts/mitigation-drying-report-data.contract.js';
import { mitigationDailyMonitoringCapability, mitigationMoldClearanceCapability, mitigationTreatmentRecordsCapability, } from './contracts/mitigation-field-completion.contract.js';
import { mitigationMoistureEvidenceCapability } from './contracts/mitigation-moisture-evidence.contract.js';
import { mitigationPsychrometricsCapability } from './contracts/mitigation-psychrometrics.contract.js';
import { mitigationContentsInventoryCapability } from './contracts/mitigation-contents-inventory.contract.js';
import { mitigationInsuranceClaimsReadinessProviderId, mitigationRoomsCapability, } from './contracts/mitigation-insurance-claims-readiness.contract.js';
import { mitigationAssistantContextCapability, mitigationAssistantContextProviderId, } from './contracts/mitigation-assistant-context.contract.js';
import { mitigationDryoutImportCapability } from './import/mitigation-dryout-import.contract.js';
export const mitigationModuleManifest = defineCrestModule({
    id: 'mitigation',
    version: '1.0.0',
    kind: 'business',
    required: false,
    requires: ['platform', 'projects'],
    requiredCapabilities: [
        'platform.tenant-context.v1',
        'platform.module-access.v1',
        'platform.membership-directory.v1',
        'platform.migration-run-ledger.v1',
        'platform.import-resolution.v1',
        'platform.storage.v1',
        platformMyDayHostCapability,
        'projects.projects.v1',
        projectsProjectRecordPolicyCapability,
        projectsProjectDetailHostCapability,
        projectsProjectDetailLauncherCapability,
        'projects.import-resolution.v1',
        'projects.project-reference-access-hold.v1',
        platformOptionalFactProviderHostCapability,
    ],
    optionalCapabilities: ['assets.placements.v1'],
    optionalCapabilityFallbacks: [{
            capability: 'assets.placements.v1',
            degradedMode: 'mitigation.degraded.assets-placements-unavailable.v1',
        }],
    provides: [
        mitigationMoistureEvidenceCapability,
        mitigationDryingWorkspaceCapability,
        mitigationDryingReadingsCapability,
        mitigationDryingSizingCapability,
        mitigationDryingReportDataCapability,
        mitigationDailyMonitoringCapability,
        mitigationTreatmentRecordsCapability,
        mitigationMoldClearanceCapability,
        mitigationPsychrometricsCapability,
        mitigationContentsInventoryCapability,
        mitigationRoomsCapability,
        mitigationAssistantContextCapability,
        mitigationDryoutImportCapability,
    ],
    permissions: [
        'mitigation.moisture-evidence.read',
        'mitigation.moisture-evidence.capture',
        'mitigation.drying.read',
        'mitigation.drying.manage',
        'mitigation.drying-readings.capture',
        'mitigation.drying-readings.void',
        'mitigation.drying-report-data.read',
        'mitigation.daily-monitoring.read',
        'mitigation.daily-monitoring.capture',
        'mitigation.daily-monitoring.submit',
        'mitigation.daily-monitoring.override',
        'mitigation.treatment-records.read',
        'mitigation.treatment-records.record',
        'mitigation.treatment-records.void',
        'mitigation.mold-clearance.read',
        'mitigation.mold-clearance.record',
        'mitigation.mold-clearance.void',
        'mitigation.psychrometrics.use',
        'mitigation.contents.read',
        'mitigation.contents.manage',
        'mitigation.contents.costs.read',
        'mitigation.contents.costs.manage',
        'mitigation.import.execute',
        'mitigation.import.rollback',
    ],
    settings: [
        'mitigation.moisture-evidence.configuration',
        'mitigation.copy.moisture-evidence',
        'mitigation.drying.configuration',
        'mitigation.drying.display',
        'mitigation.copy.drying',
        'mitigation.drying-report-data.configuration',
        'mitigation.field-completion.configuration',
        'mitigation.field-completion.display',
        'mitigation.copy.field-completion',
        'mitigation.psychrometrics.configuration',
        'mitigation.psychrometrics.display',
        'mitigation.copy.psychrometrics',
        'mitigation.contents.configuration',
        'mitigation.contents.display',
        'mitigation.copy.contents',
    ],
    copyCatalogs: [
        'mitigation.copy.moisture-evidence',
        'mitigation.copy.drying',
        'mitigation.copy.field-completion',
        'mitigation.copy.psychrometrics',
        'mitigation.copy.contents',
    ],
    events: {
        publishes: [
            'mitigation.moisture-evidence-recorded.v1',
            'mitigation.drying-site-created.v1',
            'mitigation.drying-site-updated.v1',
            'mitigation.drying-site-archived.v1',
            'mitigation.drying-site-restored.v1',
            'mitigation.drying-chamber-created.v1',
            'mitigation.drying-chamber-updated.v1',
            'mitigation.drying-chamber-archived.v1',
            'mitigation.drying-chamber-restored.v1',
            'mitigation.drying-room-created.v1',
            'mitigation.drying-room-updated.v1',
            'mitigation.drying-room-archived.v1',
            'mitigation.drying-room-restored.v1',
            'mitigation.drying-point-created.v1',
            'mitigation.drying-point-updated.v1',
            'mitigation.drying-point-archived.v1',
            'mitigation.drying-point-restored.v1',
            'mitigation.drying-reading-recorded.v1',
            'mitigation.drying-reading-corrected.v1',
            'mitigation.drying-reading-voided.v1',
            'mitigation.daily-monitoring-started.v1',
            'mitigation.daily-monitoring-item-recorded.v1',
            'mitigation.daily-monitoring-evidence-linked.v1',
            'mitigation.daily-monitoring-submitted.v1',
            'mitigation.daily-monitoring-override-submitted.v1',
            'mitigation.treatment-recorded.v1',
            'mitigation.treatment-corrected.v1',
            'mitigation.treatment-voided.v1',
            'mitigation.mold-clearance-recorded.v1',
            'mitigation.mold-clearance-corrected.v1',
            'mitigation.mold-clearance-voided.v1',
            'mitigation.contents-item-created.v1',
            'mitigation.contents-item-updated.v1',
            'mitigation.contents-item-voided.v1',
        ],
        subscribes: [],
    },
    server: {
        routes: [
            { id: 'mitigation.routes.moisture-evidence', priority: 0 },
            { id: 'mitigation.routes.drying-workspace', priority: 10 },
            { id: 'mitigation.routes.field-completion', priority: 20 },
            { id: 'mitigation.routes.drying-report-data', priority: 30 },
            { id: 'mitigation.routes.contents', priority: 40 },
        ],
        jobs: [],
        subscribers: [],
        slots: [{
                id: mitigationInsuranceClaimsReadinessProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                priority: 0,
            }, {
                id: mitigationAssistantContextProviderId,
                requiredCapability: platformOptionalFactProviderHostCapability,
                requiredPermission: 'mitigation.drying.read',
                priority: 10,
            }],
    },
    web: {
        routes: [{ id: 'mitigation.web.psychrometrics', priority: 0 }],
        navigation: [{ id: 'mitigation.navigation.psychrometrics', priority: 63 }],
        slots: [
            {
                id: 'mitigation.web.platform.my-day',
                requiredCapability: platformMyDayHostCapability,
                requiredPermission: 'mitigation.daily-monitoring.read',
                priority: 18,
            },
            {
                id: 'mitigation.web.projects.moisture-evidence',
                requiredCapability: projectsProjectDetailHostCapability,
                requiredPermission: 'mitigation.moisture-evidence.read',
                priority: 30,
            },
            {
                id: 'mitigation.web.projects.drying-workspace',
                requiredCapability: projectsProjectDetailHostCapability,
                requiredPermission: 'mitigation.drying.read',
                priority: 31,
            },
            {
                id: 'mitigation.web.projects.field-completion',
                requiredCapability: projectsProjectDetailHostCapability,
                requiredPermission: 'mitigation.daily-monitoring.read',
                priority: 32,
            },
            {
                id: 'mitigation.web.projects.contents',
                requiredCapability: projectsProjectDetailHostCapability,
                requiredPermission: 'mitigation.contents.read',
                priority: 33,
            },
        ],
    },
    mobile: {
        routes: [{ id: 'mitigation.mobile.moisture-evidence', priority: 0 }],
        slots: [],
    },
    portal: { routes: [], slots: [] },
    database: {
        schema: 'mitigation',
        owns: [
            'mitigation.moisture_evidence',
            'mitigation.drying_sites',
            'mitigation.drying_chambers',
            'mitigation.drying_rooms',
            'mitigation.drying_points',
            'mitigation.drying_workspace_versions',
            'mitigation.drying_workspace_commands',
            'mitigation.drying_days',
            'mitigation.drying_readings',
            'mitigation.drying_reading_voids',
            'mitigation.drying_reading_commands',
            'mitigation.daily_monitoring_sessions',
            'mitigation.daily_monitoring_items',
            'mitigation.daily_monitoring_commands',
            'mitigation.treatment_records',
            'mitigation.treatment_record_voids',
            'mitigation.treatment_record_commands',
            'mitigation.mold_clearance_records',
            'mitigation.mold_clearance_voids',
            'mitigation.mold_clearance_commands',
            'mitigation.contents_items',
            'mitigation.contents_item_versions',
            'mitigation.contents_item_cost_versions',
            'mitigation.contents_item_voids',
            'mitigation.contents_item_commands',
            'mitigation.dryout_import_runs',
            'mitigation.dryout_import_identity_map',
            'mitigation.dryout_import_receipts',
            'mitigation.dryout_import_rejects',
            'mitigation.dryout_import_rollbacks',
            'mitigation.dryout_import_evidence_immutable_v1',
            'mitigation.dryout_import_canonical_json_v1',
            'mitigation.dryout_import_sha256_v1',
            'mitigation.dryout_import_timestamp_v1',
            'mitigation.dryout_import_exact_keys_v1',
            'mitigation.dryout_import_uuid_v1',
            'mitigation.dryout_import_day_id_v1',
            'mitigation.dryout_import_command_id_v1',
            'mitigation.dryout_import_target_canonical_v1',
            'mitigation.dryout_import_restore_allowed_v1',
            'mitigation.dryout_import_receipt_json_v1',
            'mitigation.dryout_import_assert_membership_v1',
            'mitigation.dryout_import_assert_project_v1',
            'mitigation.begin_dryout_import_v1',
            'mitigation.quarantine_dryout_import_v1',
            'mitigation.apply_dryout_import_v1',
            'mitigation.rollback_dryout_import_v1',
        ],
        readsThrough: [
            'platform.storage.v1',
            projectsProjectRecordPolicyCapability,
            'platform.membership-directory.v1',
            'platform.migration-run-ledger.v1',
            'platform.import-resolution.v1',
            'projects.import-resolution.v1',
            'projects.project-reference-access-hold.v1',
            'assets.placements.v1',
        ],
        migrations: [
            'mitigation.moisture_evidence_golden_slice',
            'mitigation.drying-workspace-readings',
            'mitigation.field-completion',
            'mitigation.drying-report-data',
            'mitigation.psychrometrics-report-contents',
            'mitigation.dryout_import',
        ],
    },
    health: ['mitigation.database'],
    lifecycle: {
        initialization: { timeoutMs: 5_000, failureBehavior: 'quarantine-module' },
        resources: {
            requestConcurrency: 64,
            jobConcurrency: 0,
            queueBacklogLimit: 0,
            databaseConnectionBudget: 8,
            workerIsolation: 'shared-api',
        },
        disable: {
            data: 'retain',
            clientContributions: 'remove',
            serverRoutes: 'deny-tenant-access',
            scheduledJobs: 'skip',
            events: {
                behavior: 'drop-safe',
                durableCursor: false,
                retentionDays: 0,
                backlogLimit: 0,
                warningAt: 0,
                reconciliationProcedure: null,
                dropSafeReason: 'The current Mitigation slice publishes facts but owns no inbound event subscriber.',
            },
            outboundSideEffects: 'suppress-no-retroactive-send',
            inFlight: 'finish-idempotently',
        },
        resume: {
            configuration: 'last-compatible-published',
            healthChecks: ['mitigation.database'],
            reconciliationProcedure: null,
            visibility: 'after-health-and-reconciliation',
        },
        uninstall: {
            executableCode: 'remove',
            data: 'retain',
            tombstone: 'retain-immutable',
            destruction: 'separate-explicit-authority',
        },
    },
    retentionPolicy: 'Mitigation evidence remains stored and inaccessible while the optional module is disabled.',
});
//# sourceMappingURL=mitigation.module.js.map